import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import logger from '../logger';
import PipelineService from '../services/internal/PipelineService'; // Import the new service
import TierCapsService from '../services/internal/TierCapsService'; // Import TierCapsService

const PipelineContext = createContext();

export const PipelineProvider = ({ children, orgId }) => { // Accept orgId as a prop
    const [segmentProfiles, setSegmentProfiles] = useState([]);
    const [contactProfiles, setContactProfiles] = useState([]); // Default to an empty array
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchPipelineData = useCallback(async (providedOrgId) => {
        const effectiveOrgId = providedOrgId || orgId; // Use provided orgId or fallback to context orgId
        if (!effectiveOrgId) {
            // Do not attempt to fetch if orgId is not available
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await PipelineService.getPipeline(effectiveOrgId); // Use the service
            logger.info(response, "Result from pipeline API");
            const { targetSegmentProfiles, contactProfiles, accounts } = response;
            setSegmentProfiles(targetSegmentProfiles || []);
            setContactProfiles(contactProfiles || []);
            setAccounts(accounts || []);
        } catch (err) {
            logger.error('Error fetching pipeline:', err);
            setError('Failed to fetch pipeline data');
        } finally {
            setLoading(false);
        }
    }, [orgId]); // Dependencies

    useEffect(() => {
        if (orgId) {
            fetchPipelineData(orgId); // Fetch pipeline data on initialization
        }
        // Do nothing if orgId is not available
    }, [orgId, fetchPipelineData]); // Run only when orgId or fetchPipelineData changes

    const pipelineData = useMemo(() => {
        logger.info("Recomputing pipeline data");
        return segmentProfiles.map(segmentProfile => {
            const segmentAccounts = accounts.filter(account => account.targetSegmentProfileId === segmentProfile._id);
            return {
                _id: segmentProfile._id,
                propelAuthId: segmentProfile.propelAuthId,
                orgId: segmentProfile.orgId,
                name: segmentProfile.name || 'Unknown Segment',
                industry: segmentProfile.industry,
                otherIndustry: segmentProfile.industry.targetIndustry === 'Other' ? segmentProfile.otherIndustry || 'Unknown Other Industry' : undefined,
                displayIndustry: segmentProfile.displayIndustry,
                displaySubIndustry: segmentProfile.displaySubIndustry,
                companySize: segmentProfile.companySize || 'Unknown Size',
                geographicFocus: segmentProfile.geographicFocus || { geographicFocus: 'None', regions: [], states: [], countries: [], internationalRegions: [] },
                industryPainPoints: segmentProfile.industryPainPoints || [], // Include customizations
                potentialObjections: segmentProfile.potentialObjections || [], // Include customizations
                freeformCustomizations: segmentProfile.freeformCustomizations || [], // Include customizations
                archivedAt: segmentProfile.archivedAt || null,
                archivedByUserId: segmentProfile.archivedByUserId || null,
                accounts: segmentAccounts.map(account => ({
                    _id: account._id,
                    targetSegmentProfileId: account.targetSegmentProfileId,
                    propelAuthId: account.propelAuthId || null,
                    orgId: account.orgId,
                    name: account.name || 'Unknown Name',
                    industry: account.industry || 'Unknown Industry',
                    subIndustry: account.subIndustry || null,
                    size: account.size || null,
                    description: account.description || 'No description available',
                    location: account.location || 'Unknown Location',
                    difficulty: account.difficulty || 'Unknown Difficulty',
                    discoveredInformation: account.discoveredInformation || [],
                    archivedAt: account.archivedAt || null,
                    archivedByUserId: account.archivedByUserId || null,
                    active: account.active !== undefined ? account.active : true,
                    contacts: contactProfiles.map(contact => ({
                        ...contact,
                        archivedAt: contact.archivedAt || null,
                        archivedByUserId: contact.archivedByUserId || null,
                        personas: account.personas?.filter(persona => persona?.contactProfileId === contact._id).map(mapPersona) || [],
                    })),
                })),
            };
        });
    }, [segmentProfiles, accounts, contactProfiles]);

    // When mapping persona data from API responses, ensure archivedAt and archivedByUserId are included
    function mapPersona(rawPersona) {
        return {
            ...rawPersona,
            archivedAt: rawPersona.archivedAt || null,
            archivedByUserId: rawPersona.archivedByUserId || null,
        };
    }

    // Updated: Fetch suggested next call from API using PipelineService
    const getSuggestedNextCall = useCallback(async (orgIdParam, userId) => {
        const effectiveOrgId = orgIdParam || orgId;
        if (!effectiveOrgId || !userId) return null;
        try {
            return await PipelineService.getSuggestedNextCall(effectiveOrgId, userId);
        } catch (err) {
            logger.error('Error fetching suggested next call', err);
            return null;
        }
    }, [orgId]);

    const getAccountByName = (accountName) => {
        const allAccounts = pipelineData.flatMap(segment => segment.accounts);
        return allAccounts.find(account => account.name === accountName) || null;
    };

    const getAccountById = (accountId) => {
        const allAccounts = pipelineData.flatMap(segment => segment.accounts);
        return allAccounts.find(account => account._id === accountId) || null;
    };

    const getSegmentProfileById = (segmentId) => {
        return segmentProfiles.find(segment => segment._id === segmentId) || null;
    };

    const addPersonaToAccount = (accountId, newPersona) => {
        setAccounts(prevAccounts => 
            prevAccounts.map(account => 
                account._id === accountId
                    ? {
                        ...account,
                        personas: [newPersona, ...(account.personas || [])], // Add new persona to the top
                    }
                    : account
            )
        );
    };

    const addTargetSegment = async (newSegment) => {
        try {
            const addedSegment = await PipelineService.addTargetSegment(newSegment); // Call the service
            setSegmentProfiles(prevSegments => [addedSegment, ...prevSegments]); // Add new segment to the top
            return addedSegment;
        } catch (err) {
            logger.error('Error adding target segment:', err);
            setError('Failed to add target segment');
        }
    };

    const generateSegmentAccount = async (orgId, targetSegmentProfileId) => {
        try {
            logger.info({ orgId, targetSegmentProfileId }, "Generating segment account with orgId and targetSegmentProfileId"); // #file:logger.js
            const newAccount = await PipelineService.generateSegmentAccount(orgId, targetSegmentProfileId);
            setAccounts(prevAccounts => [newAccount, ...prevAccounts]); // Add new account to the top
        } catch (err) {
            logger.error('Error generating segment account:', err);
            setError('Failed to generate segment account');
        }
    };

    const saveSegmentCustomizations = async (orgId, userId, segmentId, customizations) => {
        logger.info({ orgId, userId, segmentId, customizations }, 'saveSegmentCustomizations called with:'); // #file:logger.js
        if (!orgId || !userId || !segmentId) {
            throw new Error('orgId, userId, and segmentId are required to save customizations');
        }
        try {
            const updatedSegment = await PipelineService.saveSegmentCustomizations(orgId, userId, segmentId, customizations);
            setSegmentProfiles(prevSegments =>
                prevSegments.map(segment =>
                    segment._id === segmentId ? { ...segment, ...updatedSegment } : segment
                )
            );
        } catch (error) {
            logger.error('Error saving segment customizations:', error);
            setError('Failed to save segment customizations');
        }
    };

    const addContactProfile = async (newContact) => {
        try {
            const addedContact = await PipelineService.addContactProfile(newContact); // Call the service
            setContactProfiles(prevContacts => [addedContact, ...prevContacts]); // Update state
        } catch (err) {
            logger.error('Error adding contact profile:', err);
            setError('Failed to add contact profile');
        }
    };

    const archiveAccount = async (accountId, userId) => {
        try {
            await PipelineService.archiveAccount(accountId, userId); // Call the service
            setAccounts(prevAccounts =>
                prevAccounts.map(account =>
                    account._id === accountId
                        ? { ...account, archivedAt: new Date(), archivedByUserId: userId, active: false }
                        : account
                )
            );
        } catch (error) {
            logger.error('Error archiving account:', error);
            throw error;
        }
    };

    const unarchiveAccount = async (accountId, userId) => {
        try {
            await PipelineService.unarchiveAccount(accountId, userId);
            setAccounts(prevAccounts =>
                prevAccounts.map(account =>
                    account._id === accountId
                        ? { ...account, archivedAt: null, archivedByUserId: null, active: true }
                        : account
                )
            );
        } catch (error) {
            logger.error('Error unarchiving account:', error);
            throw error;
        }
    };

    const deleteTargetSegment = async (segmentId, orgId, userId) => {
        try {
            await PipelineService.deleteTargetSegment(segmentId, orgId, userId);
            setSegmentProfiles(prevSegments =>
                prevSegments.map(segment =>
                    segment._id === segmentId
                        ? { ...segment, archivedAt: new Date(), archivedByUserId: userId }
                        : segment
                )
            );
        } catch (error) {
            logger.error('Error deleting target segment:', error);
            throw error;
        }
    };

    const updateTargetSegment = async (segmentId, updatedFields) => {
        try {
            const updatedSegment = await PipelineService.updateTargetSegment(segmentId, updatedFields);
            setSegmentProfiles(prevSegments =>
                prevSegments.map(segment =>
                    segment._id === segmentId ? { ...segment, ...updatedSegment } : segment
                )
            );
            return updatedSegment;
        } catch (err) {
            logger.error('Error updating target segment:', err);
            setError('Failed to update target segment');
            throw err;
        }
    };

    const archiveContactProfile = async (contactProfileId, userId) => {
        try {
            await PipelineService.archiveContactProfile(contactProfileId, userId);
            setContactProfiles(prevContacts =>
                prevContacts.map(contact =>
                    contact._id === contactProfileId
                        ? { ...contact, archivedAt: new Date(), archivedByUserId: userId, active: false }
                        : contact
                )
            );
        } catch (error) {
            logger.error('Error archiving contact profile:', error);
            throw error;
        }
    };

    const archivePersona = async (personaId, userId) => {
        try {
            await PipelineService.archivePersona(personaId, userId);
            setAccounts(prevAccounts =>
                prevAccounts.map(account => ({
                    ...account,
                    personas: account.personas
                        ? account.personas.map(persona =>
                            persona._id === personaId
                                ? { ...persona, archivedAt: new Date(), archivedByUserId: userId }
                                : persona
                        )
                        : []
                }))
            );
        } catch (error) {
            logger.error('Error archiving persona:', error);
            throw error;
        }
    };

    const calculateFeatureUsage = useMemo(() => {
        const accountsUsed = (accounts || []).length;
        const targetSegmentsUsed = (segmentProfiles || []).length;
        const decisionMakersUsed = (contactProfiles || []).length;
        const personasUsed = (accounts || []).reduce((acc, account) => acc + (account.personas?.length || 0), 0);

        return {
            accounts: accountsUsed,
            targetSegments: targetSegmentsUsed,
            decisionMakers: decisionMakersUsed,
            personas: personasUsed,
        };
    }, [accounts, segmentProfiles]);

    const tierCapsService = useMemo(() => new TierCapsService(calculateFeatureUsage), [calculateFeatureUsage]); // Initialize TierCapsService

    return (
        <PipelineContext.Provider value={{
            pipelineData,
            loading,
            error,
            fetchPipelineData,
            getSuggestedNextCall,
            getAccountByName,
            getAccountById, // Add getAccountById to context
            getSegmentProfileById, // Add to context
            contactProfiles,
            addPersonaToAccount,
            addTargetSegment,
            generateSegmentAccount,
            saveSegmentCustomizations,
            addContactProfile, // Add to context
            calculateFeatureUsage, // Add to context
            tierCapsService, // Provide TierCapsService instance in context
            archiveAccount, // Add archiveAccount to context
            unarchiveAccount, // Add unarchiveAccount to context
            deleteTargetSegment, // Add deleteTargetSegment to context
            updateTargetSegment, // Add updateTargetSegment to context
            archiveContactProfile, // Add to context
            archivePersona, // Add archivePersona to context
        }}>
            {children}
        </PipelineContext.Provider>
    );
};

export const usePipeline = () => {
    const context = useContext(PipelineContext);
    if (!context) {
        throw new Error('usePipeline must be used within a PipelineProvider');
    }
    return context;
};
